import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { translate, translationKeys } from 'logic/translations/translations.service';
import { elitetutorSlice } from 'logic/store/elitetutor/elitetutor.slice';
import { getFormValues } from 'redux-form';
import {
  mapMyPricingFormToSubsciptionChangeRequest,
  MY_PRICING_FORM_NAME,
  MyPricingFormValues,
} from 'src/pages-all/user/user-profile/my-pricing/my-pricing.logic';
import { Modal } from 'antd';
import {
  getElitePaymentSeekerInfo,
  getSeekerSubscriptionChangeInfoPreview,
} from 'logic/store/elitetutor/elitetutor.selectors';
import { SubChangePreview } from 'src/pages-all/user/user-profile/my-pricing/components/SubChangePreview';
import moment from 'moment';

const REMAINING_VALID_JITTER_OFFSET_SECONDS = 60; // To account for clock drift

export const MyPricingModal = () => {
  const dispatch = useDispatch();
  const elitePaymentSeekerInfo = useSelector(getElitePaymentSeekerInfo);
  const seekerSubscriptionChangeInfoPreview = useSelector(getSeekerSubscriptionChangeInfoPreview);
  const subscriptionChangeActivateAt = seekerSubscriptionChangeInfoPreview?.activateAt
    ? moment(seekerSubscriptionChangeInfoPreview.activateAt)
    : null;
  const [remainingChangeInfoValidSeconds, setRemainingChangeInfoValidSeconds] = useState<
    number | null
  >(null);
  const [showSubChangePreview, setShowSubChangePreview] = useState(false);
  const formValues: MyPricingFormValues = useSelector(getFormValues(MY_PRICING_FORM_NAME));

  useEffect(() => {
    if (seekerSubscriptionChangeInfoPreview) {
      setShowSubChangePreview(true);
    }
  }, [seekerSubscriptionChangeInfoPreview]);

  useEffect(() => {
    if (seekerSubscriptionChangeInfoPreview?.activateAt == null) {
      return;
    }
    const recomputeChangeInfoValidSeconds = () => {
      let diffSeconds = subscriptionChangeActivateAt
        ? subscriptionChangeActivateAt.diff(moment(), 'seconds') -
          REMAINING_VALID_JITTER_OFFSET_SECONDS
        : -1;
      setRemainingChangeInfoValidSeconds(diffSeconds);
      return diffSeconds;
    };
    recomputeChangeInfoValidSeconds();
    const timerId = setInterval(() => {
      const diffSeconds = recomputeChangeInfoValidSeconds();
      if (diffSeconds < 0) {
        clearTimeout(timerId);
      }
    }, 1000);
    return () => clearTimeout(timerId);
  }, [seekerSubscriptionChangeInfoPreview]);

  const handlePreviewClose = () => {
    setShowSubChangePreview(false);
    dispatch(elitetutorSlice.actions.setSubscriptionChangePreview({ response: undefined }));
  };

  const handleConfirmSubChange = () => {
    handlePreviewClose();
    if (
      subscriptionChangeActivateAt?.isBefore(
        moment().add(REMAINING_VALID_JITTER_OFFSET_SECONDS, 'seconds')
      )
    ) {
      return;
    }
    dispatch(
      elitetutorSlice.actions.subscriptionChange(
        mapMyPricingFormToSubsciptionChangeRequest(
          formValues,
          elitePaymentSeekerInfo?.subscription?.versionHash
        )
      )
    );
  };

  return (
    <Modal
      title={translate(translationKeys.pricing.subscriptionChangePreview.title)}
      visible={showSubChangePreview}
      onOk={() => handleConfirmSubChange()}
      onCancel={() => handlePreviewClose()}
      cancelText={translate(translationKeys.buttons.abort)}
      okText={translate(translationKeys.buttons.confirm)}
    >
      <SubChangePreview
        subChangeInfo={seekerSubscriptionChangeInfoPreview}
        remainingValidSeconds={remainingChangeInfoValidSeconds}
        confirmPreview
      />
    </Modal>
  );
};
